import React, { Component } from 'react';
import { Table, Button, Badge, Form, OverlayTrigger, Tooltip, Pagination, Nav, Tab } from 'react-bootstrap';
import { normalizeExpiryDate } from '../../helpers/dateUtils';

class ProductMatchPreview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      itemsPerPage: 50,
      activeTab: 'exact-matches' // exact-matches, needs-review, unmatched
    };
  }

  handlePageChange = (pageNumber) => {
    this.setState({ currentPage: pageNumber });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ 
      itemsPerPage: parseInt(e.target.value),
      currentPage: 1 
    });
  };

  handleTabChange = (tab) => {
    this.setState({ 
      activeTab: tab,
      currentPage: 1 
    });
  };

  componentDidUpdate(prevProps, prevState) {
    // If the product list or active tab changes, ensure the current page is still valid.
    // This prevents the table from appearing empty when the total number of pages decreases
    // after an upload removes items from the active tab.
    if (
      prevProps.products !== this.props.products ||
      prevState.activeTab !== this.state.activeTab ||
      prevState.itemsPerPage !== this.state.itemsPerPage
    ) {
      const products = this.props.products || [];
      const activeTab = this.state.activeTab;

      const exactMatches = products.filter(p => p.matchResult?.matchType === 'exact');
      const needsReview = products.filter(p =>
        p.matchResult?.matchType === 'normalized' ||
        p.matchResult?.matchType === 'fuzzy' ||
        p.matchResult?.matchType === 'partial' ||
        p.matchResult?.matchType === 'internal_search'
      );
      const unmatched = products.filter(p =>
        p.matchResult?.matchType === 'unmatched' ||
        p.matchResult?.matchType === 'invalid'
      );

      let activeProducts = products;
      switch (activeTab) {
        case 'exact-matches':
          activeProducts = exactMatches;
          break;
        case 'needs-review':
          activeProducts = needsReview;
          break;
        case 'unmatched':
          activeProducts = unmatched;
          break;
        default:
          activeProducts = products;
      }

      const totalPages = Math.max(1, Math.ceil(activeProducts.length / this.state.itemsPerPage));
      if (this.state.currentPage > totalPages) {
        this.setState({ currentPage: totalPages });
      }
    }
  }

  getMatchConfidenceColor = (confidence) => {
    const conf = typeof confidence === 'number' ? confidence : 0;
    if (conf >= 90) return '#28a745'; // Green
    if (conf >= 60) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  getMatchConfidenceBg = (confidence) => {
    const conf = typeof confidence === 'number' ? confidence : 0;
    if (conf >= 90) return 'success'; // Green
    if (conf >= 60) return 'warning'; // Yellow
    return 'danger'; // Red
  };

  // Convert any date format to YYYY-MM for month input - COMPREHENSIVE VERSION
  convertDateToMonthInput = (dateStr) => {
    if (!dateStr) return '';
    
    // Ensure dateStr is a string
    let dateString = String(dateStr).trim();
    if (!dateString) return '';
    
    // If already in YYYY-MM format (perfect for month input)
    if (/^\d{4}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try normalizeExpiryDate first - it handles most formats
    const normalized = normalizeExpiryDate(dateString);
    if (normalized && /^\d{2}\/\d{4}$/.test(normalized)) {
      const [month, year] = normalized.split('/');
      return `${year}-${month}`;
    }
    
    // FALLBACK: Manual parsing for edge cases
    // Remove common separators and normalize
    dateString = dateString.replace(/[-.\s]/g, '/'); // Convert dashes, dots, spaces to slashes
    
    // Try MM/YYYY format (4-digit year)
    if (/^\d{1,2}\/\d{4}$/.test(dateString)) {
      const [month, year] = dateString.split('/');
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
        return `${y}-${String(m).padStart(2, '0')}`;
      }
    }
    
    // Try MM/YY format (2-digit year)
    if (/^\d{1,2}\/\d{2}$/.test(dateString)) {
      const [month, yearStr] = dateString.split('/');
      const m = parseInt(month, 10);
      const yy = parseInt(yearStr, 10);
      
      if (m >= 1 && m <= 12) {
        let fullYear;
        if (yy >= 0 && yy <= 50) {
          fullYear = 2000 + yy; // 00-50 → 2000-2050
        } else {
          fullYear = 1900 + yy; // 51-99 → 1951-1999
        }
        return `${fullYear}-${String(m).padStart(2, '0')}`;
      }
    }
    
    // Try DD/MM/YYYY or MM/DD/YYYY (with slashes or dashes)
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const parts = dateString.split('/');
      const first = parseInt(parts[0], 10);
      const second = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      let month, day;
      // If first > 12, it's definitely DD/MM format
      // If second > 12, it's definitely MM/DD format
      // Otherwise assume DD/MM (more common internationally)
      if (first > 12) {
        day = first;
        month = second;
      } else if (second > 12) {
        month = first;
        day = second;
      } else {
        // Ambiguous - assume DD/MM
        day = first;
        month = second;
      }
      
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
        return `${year}-${String(month).padStart(2, '0')}`;
      }
    }
    
    // Try YYYY-MM-DD format
    if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('/');
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
        return `${y}-${String(m).padStart(2, '0')}`;
      }
    }
    
    // Try Excel serial number (stored as string)
    const asNum = Number(dateString);
    if (!isNaN(asNum) && asNum > 0 && asNum < 100000 && Math.floor(asNum) === asNum) {
      // It's an Excel serial number
      const normalized = normalizeExpiryDate(asNum);
      if (normalized && /^\d{2}\/\d{4}$/.test(normalized)) {
        const [month, year] = normalized.split('/');
        return `${year}-${month}`;
      }
    }
    
    // Last resort: Try to parse as any valid date
    try {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        if (year >= 1900 && year <= 2100) {
          return `${year}-${month}`;
        }
      }
    } catch (e) {
      // Ignore
    }
    
    console.warn('[convertDateToMonthInput] Could not parse date:', dateStr);
    return '';
  };

  // Get expiry date from originalData with multiple field name fallbacks
  getExpiryDate = (originalData) => {
    return originalData.expiry || 
           originalData.expiryDate || 
           originalData.expiry_date || 
           originalData.Expirydate || 
           '';
  };

  getRowClass = (matchType) => {
    switch (matchType) {
      case 'exact':
        return 'table-success';
      case 'fuzzy':
      case 'partial':
        return 'table-warning';
      case 'unmatched':
      case 'invalid':
        return 'table-danger';
      default:
        return '';
    }
  };

  getMatchBadge = (matchResult) => {
    if (!matchResult) return null;

    const { matchType, confidence } = matchResult;
    const confidencePercent = typeof confidence === 'number' ? 
      (confidence > 1 ? confidence : (confidence * 100)).toFixed(0) : 0;
    
    const bgColor = this.getMatchConfidenceBg(confidencePercent);

    switch (matchType) {
      case 'exact':
        return <Badge bg="success">✓ Exact Match (100%)</Badge>;
      case 'normalized':
        return <Badge bg={bgColor}>≈ Normalized Match ({confidencePercent}%)</Badge>;
      case 'fuzzy':
        return <Badge bg={bgColor}>~ Fuzzy Match ({confidencePercent}%)</Badge>;
      case 'partial':
        return <Badge bg={bgColor}>⚠ Partial Match ({confidencePercent}%)</Badge>;
      case 'internal_search':
        return <Badge bg={bgColor}>🔍 Similar Found ({confidencePercent}%)</Badge>;
      case 'unmatched':
        return <Badge bg="secondary">✗ New Product (Add to Stock)</Badge>;
      case 'invalid':
        return <Badge bg="danger">⚠ Invalid Data</Badge>;
      default:
        return null;
    }
  };

  renderTooltip = (message) => (
    <Tooltip id="tooltip">{message}</Tooltip>
  );

  render() {
    const { products, onEdit, onValidate, onSelectAlternative, onUploadTab, onSubmitForApproval, retailerId } = this.props;
    const { currentPage, itemsPerPage, activeTab } = this.state;

    if (!products || products.length === 0) {
      return (
        <div className="alert alert-info">
          No products to preview. Please upload an Excel file and map columns.
        </div>
      );
    }

    // Categorize products by match type
    const exactMatches = products.filter(p => 
      p.matchResult?.matchType === 'exact'
    );
    
    const needsReview = products.filter(p => 
      p.matchResult?.matchType === 'normalized' ||
      p.matchResult?.matchType === 'fuzzy' || 
      p.matchResult?.matchType === 'partial' ||
      p.matchResult?.matchType === 'internal_search'
    );
    
    const unmatched = products.filter(p => 
      p.matchResult?.matchType === 'unmatched' || 
      p.matchResult?.matchType === 'invalid'
    );

    // Get active tab products
    let activeProducts = [];
    switch (activeTab) {
      case 'exact-matches':
        activeProducts = exactMatches;
        break;
      case 'needs-review':
        activeProducts = needsReview;
        break;
      case 'unmatched':
        activeProducts = unmatched;
        break;
      default:
        activeProducts = products;
    }

    // Calculate pagination for active tab
    const totalPages = Math.ceil(activeProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, activeProducts.length);
    const currentProducts = activeProducts.slice(startIndex, endIndex);

    return (
      <div>
        {/* Tabs Navigation - Professional Card Style with Percentages */}
        <Tab.Container activeKey={activeTab} onSelect={this.handleTabChange}>
          <Nav variant="pills" className="mb-3" style={{ 
            backgroundColor: '#fff', 
            padding: '15px', 
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            gap: '10px',
            display: 'flex'
          }}>
            <Nav.Item style={{ flex: 1 }}>
              <Nav.Link 
                eventKey="exact-matches" 
                style={{ 
                  textAlign: 'center',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: activeTab === 'exact-matches' ? '#28a745' : '#f8f9fa',
                  border: activeTab === 'exact-matches' ? 'none' : '2px solid #28a745',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: activeTab === 'exact-matches' ? '#fff' : '#28a745',
                  marginBottom: '5px'
                }}>
                  {exactMatches.length}
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  fontWeight: '600',
                  color: activeTab === 'exact-matches' ? '#fff' : '#28a745',
                  marginBottom: '3px'
                }}>
                  🟢 Exact Matches
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold',
                  color: activeTab === 'exact-matches' ? 'rgba(255,255,255,0.95)' : '#28a745',
                  marginBottom: '2px'
                }}>
                  90-100%
                </div>
                <small style={{ 
                  fontSize: '11px', 
                  color: activeTab === 'exact-matches' ? 'rgba(255,255,255,0.9)' : '#6c757d'
                }}>
                  Auto-Approved • Ready to Upload
                </small>
              </Nav.Link>
            </Nav.Item>
            
            <Nav.Item style={{ flex: 1 }}>
              <Nav.Link 
                eventKey="needs-review"
                style={{ 
                  textAlign: 'center',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: activeTab === 'needs-review' ? '#ffc107' : '#f8f9fa',
                  border: activeTab === 'needs-review' ? 'none' : '2px solid #ffc107',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: activeTab === 'needs-review' ? '#fff' : '#ffc107',
                  marginBottom: '5px'
                }}>
                  {needsReview.length}
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  fontWeight: '600',
                  color: activeTab === 'needs-review' ? '#fff' : '#ffc107',
                  marginBottom: '3px'
                }}>
                  🟡 Needs Review
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold',
                  color: activeTab === 'needs-review' ? 'rgba(255,255,255,0.95)' : '#ffc107',
                  marginBottom: '2px'
                }}>
                  60-89%
                </div>
                <small style={{ 
                  fontSize: '11px', 
                  color: activeTab === 'needs-review' ? 'rgba(255,255,255,0.9)' : '#6c757d'
                }}>
                  Review & Select Alternatives • User Decision
                </small>
              </Nav.Link>
            </Nav.Item>
            
            <Nav.Item style={{ flex: 1 }}>
              <Nav.Link 
                eventKey="unmatched"
                style={{ 
                  textAlign: 'center',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: activeTab === 'unmatched' ? '#dc3545' : '#f8f9fa',
                  border: activeTab === 'unmatched' ? 'none' : '2px solid #dc3545',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: activeTab === 'unmatched' ? '#fff' : '#dc3545',
                  marginBottom: '5px'
                }}>
                  {unmatched.length}
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  fontWeight: '600',
                  color: activeTab === 'unmatched' ? '#fff' : '#dc3545',
                  marginBottom: '3px'
                }}>
                  🔴 Unmatched
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold',
                  color: activeTab === 'unmatched' ? 'rgba(255,255,255,0.95)' : '#dc3545',
                  marginBottom: '2px'
                }}>
                  &lt;60%
                </div>
                <small style={{ 
                  fontSize: '11px', 
                  color: activeTab === 'unmatched' ? 'rgba(255,255,255,0.9)' : '#6c757d'
                }}>
                  Manual Intervention • New Products
                </small>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>

        {/* Active Tab Info Banner */}
        <div className="alert mb-3" style={{
          backgroundColor: activeTab === 'exact-matches' ? '#d4edda' : 
                         activeTab === 'needs-review' ? '#fff3cd' : '#f8d7da',
          borderColor: activeTab === 'exact-matches' ? '#c3e6cb' : 
                      activeTab === 'needs-review' ? '#ffeeba' : '#f5c6cb',
          borderLeft: `4px solid ${activeTab === 'exact-matches' ? '#28a745' : 
                                   activeTab === 'needs-review' ? '#ffc107' : '#dc3545'}`,
          padding: '12px 20px',
          fontSize: '14px',
          borderRadius: '8px'
        }}>
          <strong>
            {activeTab === 'exact-matches' && '✓ Exact Matches - Auto Approved'}
            {activeTab === 'needs-review' && '⚠ Needs Review - User Decision Required (Packaging Match Highlighted)'}
            {activeTab === 'unmatched' && '✗ Unmatched Products - Add as New'}
          </strong>
          <span className="ms-3">
            {activeTab === 'exact-matches' && 'These products will be uploaded directly to stock.'}
            {activeTab === 'needs-review' && 'Review each match carefully. Packaging is a key factor in matching - alternatives with matching packaging are marked with ✓. Select from alternative suggestions if the primary match is incorrect.'}
            {activeTab === 'unmatched' && 'These products will be added as new items to the database.'}
          </span>
        </div>

        {/* Pagination Controls - Top */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="me-2">Show:</span>
            <Form.Control 
              as="select"
              value={itemsPerPage} 
              onChange={this.handleItemsPerPageChange}
              style={{ width: 'auto', display: 'inline-block' }}
              size="sm"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </Form.Control>
            <span className="ms-2">items per page</span>
          </div>
          
          <div>
            <strong>Showing {startIndex + 1}-{endIndex} of {products.length} products</strong>
          </div>
          
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => this.handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => this.handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            
            {/* Show page numbers */}
            {currentPage > 2 && <Pagination.Ellipsis disabled />}
            {currentPage > 1 && (
              <Pagination.Item onClick={() => this.handlePageChange(currentPage - 1)}>
                {currentPage - 1}
              </Pagination.Item>
            )}
            <Pagination.Item active>{currentPage}</Pagination.Item>
            {currentPage < totalPages && (
              <Pagination.Item onClick={() => this.handlePageChange(currentPage + 1)}>
                {currentPage + 1}
              </Pagination.Item>
            )}
            {currentPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            
            <Pagination.Next onClick={() => this.handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => this.handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>

        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ width: '3%' }}>#</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '18%' }}>Your Product</th>
                <th style={{ width: '14%' }}>Matched To</th>
                <th style={{ width: '9%' }}>Packaging</th>
                <th style={{ width: '8%' }}>Batch</th>
                <th style={{ width: '7%' }}>Qty</th>
                <th style={{ width: '8%' }}>MRP</th>
                <th style={{ width: '10%' }}>Expiry</th>
                <th style={{ width: '11%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.map((product, index) => {
                const matchResult = product.matchResult || {};
                const originalData = product.originalData || {};
                const globalIndex = startIndex + index;

                return (
                  <tr key={globalIndex} className={this.getRowClass(matchResult.matchType)}>
                    <td>{globalIndex + 1}</td>
                    <td>
                      {this.getMatchBadge(matchResult)}
                      {matchResult.message && (
                        <OverlayTrigger
                          placement="top"
                          overlay={this.renderTooltip(matchResult.message)}
                        >
                          <i className="bi bi-info-circle ms-2" style={{ cursor: 'pointer' }}></i>
                        </OverlayTrigger>
                      )}
                    </td>
                    <td>
                      <small><strong>{originalData.productName || 'N/A'}</strong></small>
                      {originalData.manufacturer && (
                        <><br /><small className="text-muted">{originalData.manufacturer}</small></>
                      )}
                      {originalData.form && (
                        <><br /><small className="text-muted">{originalData.form}</small></>
                      )}
                      {originalData.strength && (
                        <><br /><small className="text-muted text-secondary">{originalData.strength}</small></>
                      )}
                      {/* Show indicator ONLY for items with alternatives - NOT exact/100% matches */}
                      {matchResult.matchType !== 'exact' && 
                       matchResult.confidence !== 100 &&
                       matchResult.matchType !== 'unmatched' && 
                       matchResult.matchType !== 'invalid' &&
                       matchResult.alternatives && 
                       matchResult.alternatives.length > 0 && (
                        <><br />
                          <Badge bg="info" className="mt-1" style={{ fontSize: '10px' }}>
                            <i className="bi bi-list-ul"></i> {matchResult.alternatives.length} alternatives
                          </Badge>
                        </>
                      )}
                    </td>
                    <td>
                      {matchResult.product ? (
                        <>
                          <small><strong>{matchResult.product.medicinename}</strong></small>
                          <br />
                          <small className="text-muted">{matchResult.product.manufacturer || 'N/A'}</small>
                          {matchResult.product.form && (
                            <><br /><small className="text-muted text-secondary">{matchResult.product.form}</small></>
                          )}
                          {matchResult.product.strength && (
                            <><br /><small className="text-muted text-secondary">{matchResult.product.strength}</small></>
                          )}
                          
                          {/* EXACT MATCHES: Show success message only, NO alternatives */}
                          {(matchResult.matchType === 'exact' || matchResult.confidence === 100) ? (
                            <small className="text-success d-block mt-1" style={{ fontWeight: 600 }}>
                              <i className="bi bi-check-circle-fill"></i> Perfect match - Auto-approved
                            </small>
                          ) : null}
                          
                          {/* NEEDS REVIEW: Show alternatives dropdown for fuzzy/normalized/partial */}
                          {matchResult.matchType !== 'exact' && 
                           matchResult.confidence !== 100 &&
                           matchResult.matchType !== 'unmatched' &&
                           matchResult.matchType !== 'invalid' &&
                           matchResult.alternatives && 
                           Array.isArray(matchResult.alternatives) &&
                           matchResult.alternatives.length > 0 && (
                            <>
                              <Form.Control
                                as="select"
                                size="sm"
                                className="mt-1"
                                style={{ 
                                  backgroundColor: '#fff3cd',
                                  border: '1px solid #ffc107',
                                  fontWeight: '500'
                                }}
                                onChange={(e) => {
                                  if (e.target.value && onSelectAlternative) {
                                    const altIndex = parseInt(e.target.value);
                                    const alternative = matchResult.alternatives[altIndex];
                                    onSelectAlternative(globalIndex, {
                                      product: alternative,
                                      confidence: alternative.confidence
                                    });
                                  }
                                }}
                              >
                                <option value="">🔄 Choose Better Match ({matchResult.alternatives.length} alternatives)...</option>
                                {matchResult.alternatives.map((alt, altIdx) => {
                                  const originalPacking = originalData.packaging || originalData.Packaging || '';
                                  const altPacking = alt.packaging || alt.packaging_detail || '';
                                  const packingMatch = originalPacking && altPacking && 
                                    String(originalPacking).toLowerCase() === String(altPacking).toLowerCase();
                                  return (
                                    <option key={altIdx} value={altIdx}>
                                      {alt.medicinename} - {alt.manufacturer || 'N/A'} 
                                      {altPacking ? ` [${altPacking}]` : ''} 
                                      {packingMatch ? ' ✓ Packaging Match' : ''} 
                                      ({(alt.confidence || 0).toFixed(0)}%)
                                    </option>
                                  );
                                })}
                              </Form.Control>
                              <small className="text-info mt-1 d-block" style={{ fontWeight: 500 }}>
                                <i className="bi bi-lightbulb"></i> {matchResult.alternatives.length} similar option{matchResult.alternatives.length > 1 ? 's' : ''} available
                              </small>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      <small><strong>{originalData.packaging || originalData.Packaging || 'N/A'}</strong></small>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={originalData.batch || ''}
                        onChange={(e) => onEdit(index, 'batch', e.target.value)}
                        placeholder="Batch"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={originalData.quantity || ''}
                        onChange={(e) => onEdit(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="1"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={originalData.mrp || ''}
                        onChange={(e) => onEdit(index, 'mrp', e.target.value)}
                        placeholder="MRP"
                        step="0.01"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="month"
                        size="sm"
                        value={this.convertDateToMonthInput(this.getExpiryDate(originalData))}
                        onChange={(e) => {
                          // Convert YYYY-MM back to MM/YYYY for storage
                          const value = e.target.value;
                          if (value) {
                            const [year, month] = value.split('-');
                            const formatted = `${month}/${year}`;
                            onEdit(globalIndex, 'expiry', formatted);
                          } else {
                            onEdit(globalIndex, 'expiry', '');
                          }
                        }}
                        placeholder="MM/YYYY"
                      />
                    </td>
                    <td>
                      {(matchResult.matchType === 'unmatched' || matchResult.matchType === 'invalid') && (
                        <small className="text-muted">
                          <i className="bi bi-flag"></i> Will be auto-created with validation flag
                        </small>
                      )}
                      {matchResult.matchType === 'partial' && (
                        <small className="text-warning">
                          <i className="bi bi-exclamation-triangle"></i> Review recommended
                        </small>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Bottom Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center mt-3 mb-3">
              <Pagination size="sm">
                <Pagination.First 
                  onClick={() => this.handlePageChange(1)} 
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => this.handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 1}
                />
                
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => this.handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                    return <Pagination.Ellipsis key={pageNum} disabled />;
                  }
                  return null;
                })}

                <Pagination.Next 
                  onClick={() => this.handlePageChange(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => this.handlePageChange(totalPages)} 
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>

        {/* Tab-wise Upload/Download Button */}
        {activeProducts.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <div>
              <strong>
                {activeTab === 'unmatched' 
                  ? `${activeProducts.length} unmapped products - Send for Admin Approval` 
                  : `Ready to upload ${activeProducts.length} products from this tab`}
              </strong>
              <div className="mt-1">
                <small className="text-muted">
                  {activeTab === 'exact-matches' && 'These will be added directly to stock with existing product IDs'}
                  {activeTab === 'needs-review' && 'Review carefully before uploading. Check alternatives if needed.'}
                  {activeTab === 'unmatched' && '⚠️ Cannot upload unmapped products. Submit for admin approval to add to master database.'}
                </small>
              </div>
            </div>
            {activeTab === 'unmatched' ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => onSubmitForApproval && onSubmitForApproval(activeProducts)}
                  style={{ minWidth: '250px' }}
                >
                  <i className="bi bi-send"></i> Send for Admin Approval
                </Button>
              </div>
            ) : (
              <Button
                variant={activeTab === 'exact-matches' ? 'success' : 'warning'}
                size="lg"
                onClick={() => onUploadTab && onUploadTab(activeTab, activeProducts)}
                style={{ minWidth: '200px' }}
              >
                <i className="bi bi-upload"></i> Upload {activeProducts.length} Products
              </Button>
            )}
          </div>
        )}

        </Tab.Content>
        </Tab.Container>
      </div>
    );
  }
}

export default ProductMatchPreview;
